import type { DataTableFilterMeta } from 'primereact/datatable';
import { flattenFilterMetaToConstraints } from '@/helpers/pipelineBuilders.helpers';
import {
  conditionGroupFromJson,
  conditionFields,
  conditionSentence,
  describeConditionOperator,
} from '@/helpers/conditionTree.helpers';
import type { ConditionNode } from '@/helpers/conditionTree.helpers';
import { isAnalysisGeneratedField } from '@/helpers/analysisMadeStep.helpers';
import { analysisBreakdownArbitraryGroupWarning } from '@/helpers/analysisBreakdown.helpers';
import type { AnalysisColumnDisplayMap } from '@/helpers/analysisColumnDisplay.helpers';
import type {
  AnalysisRecipeState,
  AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import { ANALYSIS_RECIPE_SCHEMA_VERSION } from '@/types/analysisRecipe.types';
import { DEFAULT_REMAP_SEARCH_LIMIT } from '@/constants/pipelines.constants';
import { searchConditionSummary } from '@/helpers/searchSheet.helpers';

export type AnalysisRecipeGroupName =
  | 'Source'
  | 'Narrowed'
  | 'Made'
  | 'Kept'
  | 'Grouped'
  | 'Controls'
  | 'Sorted';

export interface AnalysisRecipeGroupItem {
  id: string;
  label: string;
  /**
   * Full detail for the chip's tooltip when the label is a summary. A grouped
   * sheet carries one aggregation per numeric column, so enumerating them all in
   * the chip makes it unreadable; the enumeration belongs here.
   */
  tooltip?: string;
  stepKind?: 'lookup' | 'rollup' | 'calc' | 'join';
  /**
   * Column this chip filters, when the chip *is* a single column filter. Carried
   * so the chip can offer "Make this a parameter" without re-parsing its id — a
   * promoted control needs a field, and promotion belongs on the filter it
   * promotes rather than on a header button that had to guess which one.
   */
  field?: string;
  /**
   * Set on the Grouped chip when the grouping is a to-many lookup's own output:
   * the lookup already collapsed each row's matches to one arbitrary pick, so
   * which group a row lands in — and the group totals themselves — are not the
   * same run to run. Text doubles as the badge's tooltip.
   */
  warning?: string;
}

export interface AnalysisRecipeGroup {
  name: AnalysisRecipeGroupName;
  items: AnalysisRecipeGroupItem[];
}

export interface AnalysisTableSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface AnalysisSheetTransientContext {
  filters?: DataTableFilterMeta;
  sort?: AnalysisTableSort | null;
}

export interface AnalysisFamilyWorkset {
  rootSheetId: string | null;
  sheetIds: string[];
}

export interface AnalysisDiscardPlan {
  affectedSheetIds: string[];
  affectedLabels: string[];
  /** View-state slots to clear — see `analysisViewStateKey`. */
  affectedViewKeys: string[];
  itemCount: number;
}

const activeFilterFields = (
  filters: AnalysisSheetTransientContext['filters'],
): string[] => [
  ...new Set(
    flattenFilterMetaToConstraints(filters)
      .filter(
        (constraint) => String(constraint.value ?? '').trim() !== '',
      )
      .map((constraint) => constraint.field),
  ),
];

const sourceLabel = (sheet: AnalysisSheetRecipe): string => {
  const search = sheet.source.search;
  if (search && sheet.source.label) {
    // Naming the query is the point: a sheet sourced from a search is not the
    // whole label, and a chip reading just the label would say it was.
    const how = search.mode === 'semantic' ? 'meaning' : 'search';
    // Every condition is named, not just the query. Two sheets differing only
    // by a filter are two different row sets, and this chip is the only place
    // that says which one is on screen — naming the query alone made both of
    // them claim to be the whole of `db2`.
    const conditions = searchConditionSummary(search);
    // Says "top N" because it IS a top N: the search step is capped, so these
    // rows are the best matches, not every match. A sheet that counted or
    // charted them as the whole population would be wrong, and nothing else in
    // the sheet chrome reports the cap.
    return (
      `${sheet.source.label} matching “${search.query}” ` +
      `(${[how, ...conditions, `top ${DEFAULT_REMAP_SEARCH_LIMIT}`].join(', ')})`
    );
  }
  return (
    sheet.source.label ??
    (sheet.source.kind === 'exploration' ? 'Exploration' : 'Source')
  );
};

export function findRootAnalysisSheetForLabel(
  sheets: AnalysisRecipeState['sheets'],
  label: string,
): AnalysisSheetRecipe | null {
  return (
    Object.values(sheets).find(
      (sheet) =>
        sheet.breakdown === null &&
        sheet.source.kind === 'label' &&
        sheet.source.label === label &&
        // A search-sourced sheet shows a slice of the label, not the label, so
        // it must never answer as its root. It owns its own view-state slot;
        // letting it answer here pointed the label's fetch options and refresh
        // at the wrong slot.
        sheet.source.search === undefined,
    ) ?? null
  );
}

export function findAnalysisSheetForLabel(
  state: AnalysisRecipeState,
  label: string,
): AnalysisSheetRecipe | null {
  const activeSheet = state.activeSheetId
    ? state.sheets[state.activeSheetId]
    : undefined;
  if (activeSheet?.source.label === label) return activeSheet;
  if (activeSheet?.face === 'analysis') {
    const family = scopeAnalysisFamilyWorkset(state, activeSheet.id);
    const familySheet = family.sheetIds
      .map((sheetId) => state.sheets[sheetId])
      .find((sheet) => sheet?.source.label === label);
    if (familySheet) return familySheet;
  }
  return findRootAnalysisSheetForLabel(state.sheets, label);
}

/**
 * Which slot a sheet's sort and column filters live in.
 *
 * A derived sheet inherits its parent's `source` verbatim, so keying view state
 * by entity label makes a breakdown and its parent share one slot: sorting the
 * grouped sheet re-sorts the source tab, and a sort on a grouped-only column
 * hands the source pipeline an `order_by` for a column it does not project,
 * failing the run. Sorting or filtering a breakdown is a statement about groups,
 * so a derived sheet owns its own slot.
 *
 * Root sheets stay keyed by label. That keeps saved views readable without a
 * schema bump, since what they persist is already label-keyed. A sheet id is
 * never a bare entity label, so the two key spaces cannot collide.
 */
export function analysisViewStateKey(
  sheet: AnalysisSheetRecipe | null | undefined,
  label?: string,
): string | undefined {
  if (!sheet) return label;
  if (sheet.breakdown || sheet.worksetRootSheetId) return sheet.id;
  return label ?? sheet.source.label ?? undefined;
}

/**
 * The sheet whose grain the entity fetch serves: the nearest ancestor that is
 * not a breakdown, or the sheet itself.
 *
 * The row fetch always returns source-grain rows, so a breakdown's own sort and
 * filters must never reach it. Deliberately not `familyRootId`, which walks all
 * the way to the family root — the stop condition here is the first
 * non-breakdown ancestor. The walk is bounded because a cycle would otherwise
 * hang the fetch.
 */
export function analysisSourceSheetOf(
  state: AnalysisRecipeState,
  sheet: AnalysisSheetRecipe,
): AnalysisSheetRecipe {
  let current = sheet;
  const visited = new Set<string>();
  while (current.breakdown && !visited.has(current.id)) {
    visited.add(current.id);
    const parent = state.sheets[current.breakdown.parentSheetId];
    if (!parent) return current;
    current = parent;
  }
  return current;
}

/**
 * Whether a sheet produces rows of its OWN, rather than showing the label's page.
 *
 * `steps.length === 0` is not the same as "no recipe". A search sheet has no
 * steps and yet its rows ARE the search's hits — so treating it as step-less
 * made the grid fall back to the record type's plain page, and a tab named
 * `Identity · db2` showed all 20,229 Identities until the search run landed.
 */
export function sheetHasOwnResult(sheet: AnalysisSheetRecipe): boolean {
  return (
    sheet.steps.length > 0 ||
    sheet.breakdown !== null ||
    sheet.source.search !== undefined
  );
}

export function hasDurableAnalysisArtifacts(
  sheet: AnalysisSheetRecipe,
  allSheets?: AnalysisRecipeState['sheets'],
): boolean {
  const hasChildBreakdown =
    allSheets !== undefined &&
    Object.values(allSheets).some(
      (candidate) => candidate.breakdown?.parentSheetId === sheet.id,
    );

  return (
    sheet.steps.length > 0 ||
    sheet.keepFilter !== null ||
    sheet.controls.length > 0 ||
    sheet.charts.length > 0 ||
    sheet.breakdown !== null ||
    // A search source is durable on its own: it is the whole reason the sheet
    // exists, and it is not recoverable from anything else. Without this a
    // freshly made search sheet has no steps, no filter, no charts — so the
    // capture read the recipe as empty and saved no recipe at all, and the
    // sheet was gone on the next restore with nothing said.
    sheet.source.search !== undefined ||
    hasChildBreakdown
  );
}

export function buildAnalysisRecipeGroups(
  sheet: AnalysisSheetRecipe,
  transient: AnalysisSheetTransientContext = {},
  display?: AnalysisColumnDisplayMap,
  /** Needed only to detect an arbitrary to-many-lookup grouping; see below. */
  parentSheet?: AnalysisSheetRecipe | null,
): AnalysisRecipeGroup[] {
  // Every field a chip prints may be a computed column, whose field *is* an
  // encoded workset key. Nothing derives a heading from the key itself, so a
  // chip without this map shows the author an implementation detail.
  const labelFor = (field: string): string => display?.[field]?.label ?? field;
  const keepTree = conditionGroupFromJson(sheet.keepFilter);
  const groupedColumns = new Set([
    ...(sheet.breakdown?.aggregations ?? []).map(
      (aggregation) => aggregation.outputColumn,
    ),
    ...(sheet.breakdown?.windows ?? []).map((window) => window.outputColumn),
  ]);
  const madeColumns = new Set(
    sheet.steps.flatMap((step) => step.outputColumns),
  );
  const isPostSourceCondition = (condition: ConditionNode): boolean =>
    condition.type !== 'related' &&
    conditionFields(condition).some(
      (field) =>
        isAnalysisGeneratedField(field) ||
        madeColumns.has(field) ||
        groupedColumns.has(field),
    );
  const narrowedConditions = keepTree.conditions.filter(
    (condition) => !isPostSourceCondition(condition),
  );
  const keptConditions = keepTree.conditions.filter(isPostSourceCondition);
  const transientFields = activeFilterFields(transient.filters);
  const narrowedItems = [
    ...transientFields
      .filter(
        (field) =>
          !isAnalysisGeneratedField(field) &&
          !madeColumns.has(field) &&
          !groupedColumns.has(field),
      )
      .map((field) => ({
        id: `filter:${field}`,
        label: labelFor(field),
        field,
      })),
    ...narrowedConditions.map((condition, index) => ({
      id: `condition:${index}`,
      label: conditionSentence(condition, labelFor),
    })),
  ];
  const keptItems = [
    ...transientFields
      .filter(
        (field) =>
          isAnalysisGeneratedField(field) ||
          madeColumns.has(field) ||
          groupedColumns.has(field),
      )
      .map((field) => ({
        id: `filter:${field}`,
        label: labelFor(field),
        field,
      })),
    ...keptConditions.map((condition, index) => ({
      id: `condition:${index}`,
      label: conditionSentence(condition, labelFor),
    })),
    ...(sheet.keepFilter && sheet.keepFilter.type !== 'group'
      ? [{ id: 'legacy-keep-filter', label: 'Keep filtered rows' }]
      : []),
  ];
  // A breakdown carries one aggregation per numeric column, so enumerating them
  // all produced a chip wider than the bar. The chip counts them; the tooltip
  // still names every one.
  const aggregations = sheet.breakdown?.aggregations ?? [];
  const aggregationLabel = (
    aggregation: (typeof aggregations)[number],
  ): string =>
    display?.[aggregation.outputColumn]?.label ??
    (aggregation.field
      ? `${labelFor(aggregation.field)} ${aggregation.op}`
      : `rows ${aggregation.op}`);
  const rowCountAggregations = aggregations.filter(
    (aggregation) => !aggregation.field,
  );
  const summaryAggregations = aggregations.filter(
    (aggregation) => aggregation.field,
  );
  // A related-record grouping's `groupByColumn` is a freshly minted encoded
  // key with no parent column behind it — `labelFor` would print the raw
  // `analysis__…` key — so its own `targetLabel` is the readable name instead.
  // A total sheet has no group column at all, so `labelFor` must not be reached
  // with `undefined` — it would print the literal string "undefined" as the
  // thing the sheet is grouped by.
  const groupByColumn = sheet.breakdown?.groupByColumn;
  const groupLabel = sheet.breakdown?.groupByRelated
    ? sheet.breakdown.groupByRelated.targetLabel
    : groupByColumn
      ? labelFor(groupByColumn)
      : '';
  /** "Grouped by X", or "Totalled" when there is no key to group by. */
  const grainLabel = groupLabel ? `Grouped by ${groupLabel}` : 'Totalled';
  const breakdownRecipe = sheet.breakdown
    ? [
        grainLabel,
        ...(rowCountAggregations.length > 0 ? ['count'] : []),
        ...(summaryAggregations.length > 0
          ? [
              `${summaryAggregations.length} ${
                summaryAggregations.length === 1 ? 'summary' : 'summaries'
              }`,
            ]
          : []),
      ].join(' · ')
    : null;
  const breakdownTooltip = sheet.breakdown
    ? [grainLabel, ...aggregations.map(aggregationLabel)].join(
        ' · ',
      )
    : undefined;
  const breakdownWarning = analysisBreakdownArbitraryGroupWarning(
    sheet,
    parentSheet ?? null,
  );
  const groups: AnalysisRecipeGroup[] = [
    {
      name: 'Source',
      items: [{ id: 'source', label: sourceLabel(sheet) }],
    },
    {
      name: 'Narrowed',
      items: narrowedItems,
    },
    {
      name: 'Made',
      items: sheet.steps.map((step) => ({
        id: step.id,
        label: step.sentence,
        stepKind: step.kind,
      })),
    },
    {
      name: 'Kept',
      items: keptItems,
    },
    {
      name: 'Grouped',
      items: sheet.breakdown
        ? [
            {
              id: 'breakdown',
              label: breakdownRecipe ?? '',
              ...(breakdownTooltip && breakdownTooltip !== breakdownRecipe
                ? { tooltip: breakdownTooltip }
                : {}),
              ...(breakdownWarning ? { warning: breakdownWarning } : {}),
            },
          ]
        : [],
    },
    {
      name: 'Controls',
      items: sheet.controls.map((control) => ({
        id: control.id,
        label: `${labelFor(control.field)} ${describeConditionOperator(
          control.operator,
        )} ${String(control.value)}`,
        field: control.field,
      })),
    },
    {
      name: 'Sorted',
      items: transient.sort
        ? [
            {
              id: 'sort',
              label: `${labelFor(transient.sort.field)} ${
                transient.sort.direction === 'asc' ? 'ascending' : 'descending'
              }`,
            },
          ]
        : [],
    },
  ];

  return groups.filter((group) => group.items.length > 0);
}

/**
 * Counts each independently cleared item once: every recipe step and control,
 * the keep filter and breakdown link when present, each chart, each active
 * table-filter field, and one item for an active sort.
 */
export function computeDiscardItemCount(
  sheet: AnalysisSheetRecipe,
  transient: AnalysisSheetTransientContext = {},
): number {
  return (
    sheet.steps.length +
    sheet.controls.length +
    (sheet.keepFilter ? 1 : 0) +
    (sheet.breakdown ? 1 : 0) +
    sheet.charts.length +
    activeFilterFields(transient.filters).length +
    (transient.sort ? 1 : 0)
  );
}

export function buildAnalysisDiscardPlan(
  state: AnalysisRecipeState,
  targetSheetId: string,
  /** Transient view state keyed by `analysisViewStateKey`, not by label. */
  transientByViewKey: Record<string, AnalysisSheetTransientContext> = {},
): AnalysisDiscardPlan {
  if (!state.sheets[targetSheetId]) {
    return {
      affectedSheetIds: [],
      affectedLabels: [],
      affectedViewKeys: [],
      itemCount: 0,
    };
  }

  const affectedSheetIds: string[] = [];
  const pendingSheetIds = [targetSheetId];
  const visited = new Set<string>();
  while (pendingSheetIds.length > 0) {
    const sheetId = pendingSheetIds.shift();
    if (!sheetId || visited.has(sheetId)) continue;
    visited.add(sheetId);
    affectedSheetIds.push(sheetId);
    Object.values(state.sheets).forEach((sheet) => {
      if (
        sheet.breakdown?.parentSheetId === sheetId ||
        sheet.worksetRootSheetId === sheetId
      ) {
        pendingSheetIds.push(sheet.id);
      }
    });
  }

  const affectedLabels = [
    ...new Set(
      affectedSheetIds
        .map((sheetId) => state.sheets[sheetId]?.source.label)
        .filter((label): label is string => Boolean(label)),
    ),
  ];
  const affectedViewKeys = [
    ...new Set(
      affectedSheetIds
        .map((sheetId) =>
          analysisViewStateKey(
            state.sheets[sheetId],
            state.sheets[sheetId]?.source.label ?? undefined,
          ),
        )
        .filter((key): key is string => Boolean(key)),
    ),
  ];
  const persistedItemCount = affectedSheetIds.reduce((count, sheetId) => {
    const sheet = state.sheets[sheetId];
    return sheet ? count + computeDiscardItemCount(sheet) : count;
  }, 0);
  const transientItemCount = affectedViewKeys.reduce((count, viewKey) => {
    const transient = transientByViewKey[viewKey];
    return (
      count +
      activeFilterFields(transient?.filters).length +
      (transient?.sort ? 1 : 0)
    );
  }, 0);

  return {
    affectedSheetIds,
    affectedLabels,
    affectedViewKeys,
    itemCount: persistedItemCount + transientItemCount,
  };
}

const familyRootId = (
  sheetId: string,
  sheets: AnalysisRecipeState['sheets'],
): string => {
  let currentId = sheetId;
  const visited = new Set<string>();

  while (!visited.has(currentId)) {
    visited.add(currentId);
    const sheet = sheets[currentId];
    const parentId =
      sheet?.breakdown?.parentSheetId ?? sheet?.worksetRootSheetId;
    if (!parentId || !sheets[parentId]) return currentId;
    currentId = parentId;
  }

  return sheetId;
};

/**
 * A sheet as a recipe of its own: active, with the sheets of its family it is
 * compiled with and nothing else — what a card keeps so reMap can reopen it.
 */
export function analysisSourceRecipe(
  sheet: AnalysisSheetRecipe,
  sheets: AnalysisRecipeState['sheets'] = {},
): AnalysisRecipeState {
  const all = { ...sheets, [sheet.id]: sheet };
  const { sheetIds } = scopeAnalysisFamilyWorkset(
    {
      schemaVersion: ANALYSIS_RECIPE_SCHEMA_VERSION,
      activeSheetId: sheet.id,
      sheetOrder: Object.keys(all),
      sheets: all,
    },
    sheet.id,
  );
  return {
    schemaVersion: ANALYSIS_RECIPE_SCHEMA_VERSION,
    activeSheetId: sheet.id,
    sheetOrder: sheetIds,
    sheets: Object.fromEntries(sheetIds.map((id) => [id, all[id]])),
  };
}

export function scopeAnalysisFamilyWorkset(
  state: AnalysisRecipeState,
  activeSheetId: string | null = state.activeSheetId,
): AnalysisFamilyWorkset {
  if (!activeSheetId || !state.sheets[activeSheetId]) {
    return {
      rootSheetId: null,
      sheetIds: [...state.sheetOrder],
    };
  }

  const rootSheetId = familyRootId(activeSheetId, state.sheets);
  const sheetIds = state.sheetOrder.filter(
    (sheetId) => familyRootId(sheetId, state.sheets) === rootSheetId,
  );

  return {
    rootSheetId,
    sheetIds,
  };
}

/**
 * Action ids the generic table toolbar must not render while the Analysis sheet
 * header is present, because the header owns them.
 *
 * `filter` is listed for a different reason than the rest: the header does not
 * render a filter button either. Filtering is a column-level action reached from
 * the column menu, so suppressing the generic button leaves no filter button at
 * all on an Analysis sheet — which is the intent. The generic action stays live
 * outside schema view, where its overlay is the only filter UI.
 */
export const ANALYSIS_HEADER_OWNED_ACTION_IDS = [
  'create-card',
  'export',
  'clear-table',
  'filter',
  'columns',
] as const;

/**
 * Drops the actions the Analysis sheet header already owns.
 *
 * Both toolbars live in one `flex-nowrap` row whose groups are `shrink-0`, so
 * rendering both sets does not wrap or truncate — the second set draws on top
 * of the first. The header shows the same group on the Data and Analysis faces
 * alike, so this is keyed on the header being present, never on the face.
 */
export function visibleTablePanelActions<T extends { id: string }>(
  actions: T[],
  hasAnalysisHeader: boolean,
): T[] {
  if (!hasAnalysisHeader) return actions;
  const owned = new Set<string>(ANALYSIS_HEADER_OWNED_ACTION_IDS);
  return actions.filter((action) => !owned.has(action.id));
}
